import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";

export function WorksiteVisitReports({ wsId, canManage }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pick, setPick] = useState("");
  const load = useCallback(() => { api.get(`/track/worksites/${wsId}/visit-reports`).then((r) => setData(r.data)).catch(() => setData({ linked: [], candidates: [] })); }, [wsId]);
  useEffect(() => { load(); }, [load]);
  const link = async () => {
    const [kind, id] = pick.split(":");
    try {
      const { data: res } = await api.post(`/track/worksites/${wsId}/visit-reports`, { kind, id });
      toast.success(res.published ? "Rapport ajouté au chantier — visible par le client" : "Rapport ajouté au chantier — pas encore publié au client");
      setPick(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const unlink = async (r) => { try { await api.delete(`/track/worksites/${wsId}/visit-reports/${r.kind}/${r.id}`); toast.success("Rapport retiré du chantier"); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!data) return null;
  return (
    <Card className="p-4" data-testid="ws-visit-reports">
      <h2 className="mb-3 font-display text-base font-semibold text-[#0D2E24]">Rapports de visite</h2>
      {data.linked.length === 0 ? <p className="text-sm text-slate-400" data-testid="ws-visit-reports-empty">Aucun rapport de visite lié.</p> : (
        <ul className="space-y-1.5">{data.linked.map((r) => (
          <li key={`${r.kind}-${r.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 p-2.5 text-sm" data-testid={`ws-visit-report-${r.id}`}>
            <span className="flex items-center gap-2"><Icons.ClipboardCheck className="h-4 w-4 text-emerald-600" />{r.label}{r.date ? ` — ${fmtDate(r.date)}` : ""}</span>
            <span className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${r.published ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid={`ws-visit-report-state-${r.id}`}>{r.published ? "Visible par le client" : "Non publié — invisible pour le client"}</span>
              {r.visit_id && <Button size="sm" variant="ghost" onClick={() => navigate(`/check/${r.visit_id}`)} data-testid={`ws-visit-report-open-${r.id}`}>{r.published ? "Ouvrir" : "Ouvrir pour publier"}</Button>}
              {canManage && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => unlink(r)} data-testid={`ws-visit-report-unlink-${r.id}`}><Icons.X className="h-4 w-4" /></Button>}
            </span>
          </li>))}</ul>
      )}
      {canManage && (
        data.candidates.length === 0 ? <p className="mt-3 text-xs text-slate-400">Aucun autre rapport de visite validé pour ce site.</p> : (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger className="w-full sm:w-80" data-testid="ws-visit-report-select"><SelectValue placeholder="Choisir un rapport de visite du site" /></SelectTrigger>
              <SelectContent>{data.candidates.map((c) => <SelectItem key={`${c.kind}:${c.id}`} value={`${c.kind}:${c.id}`}>{c.label}{c.date ? ` — ${fmtDate(c.date)}` : ""}{c.published ? "" : " (non publié)"}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" onClick={link} disabled={!pick} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ws-visit-report-add"><Icons.Plus className="mr-1 h-4 w-4" />Ajouter au chantier</Button>
          </div>)
      )}
    </Card>
  );
}
